import React from 'react';
import { Box, Typography, IconButton, Checkbox } from '@mui/material';
import RGL, { WidthProvider } from 'react-grid-layout';
import { SectionWithId } from './MetadataInterfaces';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveIcon from '@mui/icons-material/OpenWith';

const ReactGridLayout = WidthProvider(RGL);

interface SectionListProps {
  sections: SectionWithId[];
  selectedSection: SectionWithId | null;
  onSectionClick: (section: SectionWithId) => void;
  onUpdateSections: (updatedSections: SectionWithId[]) => void;
  onEditSection: (section: SectionWithId) => void;
  onDeleteSection: (sectionId: string) => void;
  onMoveSection: (section: SectionWithId) => void;
  isSelectionMode: boolean;
  selectedItems: string[];
  onSelectionChange: (id: string) => void;
}

const SectionList: React.FC<SectionListProps> = ({
  sections,
  selectedSection,
  onSectionClick,
  onUpdateSections,
  onEditSection,
  onDeleteSection,
  onMoveSection,
  isSelectionMode,
  selectedItems,
  onSelectionChange,
}) => {
  const onLayoutChange = (layout: RGL.Layout[]) => {
    const newSections = layout
      .sort((a, b) => a.y - b.y)
      .map((item) => sections.find((s) => s.id === item.i))
      .filter((section): section is SectionWithId => section !== undefined);

    onUpdateSections(newSections);
  };

  const handleMouseDown = (e: React.MouseEvent, section: SectionWithId) => {
    if (!(e.target as HTMLElement).closest('.drag-handle')) {
      e.preventDefault();
      onSectionClick(section);
    }
  };

  return (
    <ReactGridLayout
      className="layout"
      layout={sections.map((section, index) => ({
        i: section.id,
        x: 0,
        y: index,
        w: 1,
        h: 1,
      }))}
      cols={1}
      rowHeight={40}
      width={250}
      onLayoutChange={onLayoutChange}
      isResizable={false}
      draggableHandle=".drag-handle"
    >
      {sections.map((section) => (
        <div key={section.id}>
          <Box
            onMouseDown={(e) => handleMouseDown(e, section)}
            sx={{
              p: 1,
              height: '100%',
              cursor: 'pointer',
              bgcolor:
                selectedSection?.id === section.id
                  ? 'action.selected'
                  : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0, // This allows the text to shrink
                flex: 1,
              }}
            >
              {isSelectionMode && (
                <Checkbox
                  checked={selectedItems.includes(section.id)}
                  onChange={() => onSelectionChange(section.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {section.name || <em>EMPTY_SECTION</em>}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <IconButton onClick={() => onMoveSection(section)} size="small">
                <MoveIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => onEditSection(section)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => onDeleteSection(section.id)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton className="drag-handle" size="small">
                ⋮
              </IconButton>
            </Box>
          </Box>
        </div>
      ))}
    </ReactGridLayout>
  );
};

export default SectionList;
